/**
 * Quantity Discount Selector
 * Handles quantity-based bulk discounts (sequential/additive) with Loop subscription integration
 *
 * Discount tiers are SEQUENTIAL: each tier's percentageOff is summed with all
 * previous tiers. The cumulative total is stored in data-percentage-off by Liquid.
 */
(function() {
  // Wait for DOM to be ready before initializing (form may come after this script)
  function init() {
    document.querySelectorAll('[data-qty-discount-selector]').forEach(initSelector);
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded, but form might still be rendering - use setTimeout
    setTimeout(init, 0);
  }
  
  function initSelector(selector) {
    const inputs = selector.querySelectorAll('[data-qty-discount-input]');
    const options = selector.querySelectorAll('.qty-discount-selector__option');
    const hiddenQtyInput = selector.querySelector('[data-qty-discount-quantity]');
    
    // Find the product container and form
    const productContainer = selector.closest('[data-product]') || selector.closest('.product-single__wrapper');
    const form = productContainer?.querySelector('form[action*="/cart/add"]') || selector.closest('form');
    
    // Find ALL quantity inputs in the form and update them all
    const allQtyInputs = form ? form.querySelectorAll('[name="quantity"]') : [];
    
    // Find price display elements
    const priceElements = productContainer?.querySelectorAll('[data-product-price]');
    const atcButton = productContainer?.querySelector('[data-add-to-cart]');
    
    // Store original base price (one-time purchase price)
    const originalBasePrice = parseInt(selector.dataset.basePrice) || 0;
    const liquidSubscriptionPrice = parseInt(selector.dataset.subscriptionPrice) || 0;
    let basePrice = originalBasePrice;
    let isSubscription = false;
    let subscriptionPrice = null;

    // Per-variant count data (base_count / count_label)
    const productId = selector.dataset.productId;
    let currentVariantId = selector.dataset.variantId;
    const variantCounts = (window.chiefVariantCounts && productId) ? window.chiefVariantCounts[productId] : null;

    function getVariantCountData(variantId) {
      if (!variantCounts || !variantId) return null;
      return variantCounts[variantId] || null;
    }

    // Parse "singular,plural" (or single word) into [singular, plural].
    function parseQtyName(raw) {
      if (!raw) return null;
      const parts = String(raw).split(',').map(s => s.trim()).filter(Boolean);
      if (!parts.length) return null;
      return { singular: parts[0], plural: parts[1] || parts[0] };
    }

    let _initialCountData = getVariantCountData(currentVariantId);
    let currentBaseCount = _initialCountData ? _initialCountData.base_count : 0;
    let currentCountLabel = _initialCountData ? _initialCountData.count_label : null;
    let currentQtyName = _initialCountData ? parseQtyName(_initialCountData.qty_name) : null;
    
    // Remove our hidden input's name to avoid duplicates - we'll update the form's existing input
    if (hiddenQtyInput && allQtyInputs.length > 1) {
      hiddenQtyInput.removeAttribute('name');
    }
    
    // Get the first quantity input (the one that will be submitted)
    const qtyInput = allQtyInputs[0] || hiddenQtyInput;
    
    // Override add to cart to bypass any app interference
    if (form) {
      const addBtn = form.querySelector('[data-add-to-cart]');
      if (addBtn) {
        // Clone button to remove ALL existing event listeners
        const newBtn = addBtn.cloneNode(true);
        addBtn.parentNode.replaceChild(newBtn, addBtn);
        
        newBtn.addEventListener('click', function(e) {
          // For sold-out variants: only preventDefault, let event bubble to
          // document so theme's delegated cartAddEvent handler opens the popup.
          // (stopPropagation would kill that handler before it runs.)
          const formWrapper = newBtn.closest('[data-form-wrapper]');
          const isVariantSoldOut = formWrapper?.classList.contains('variant--soldout');
          const hasNotificationPopup = newBtn.hasAttribute('data-notification-popup');
          if (isVariantSoldOut && hasNotificationPopup) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          // Get the current quantity from our selector
          const checkedInput = selector.querySelector('[data-qty-discount-input]:checked');
          const quantity = checkedInput ? parseInt(checkedInput.dataset.quantity) : 1;
          
          // Get variant ID
          const variantInput = form.querySelector('[name="id"]');
          const variantId = variantInput ? variantInput.value : null;
          
          // Get selling plan if subscription is selected
          // First check Loop widget (uses select dropdown), then fallback to radio buttons
          let sellingPlan = null;
          
          // Find Loop widget - may be outside productContainer
          const findLoopWidgetForCart = () => {
            let widget = productContainer?.querySelector('.loop-widget-container');
            if (widget) return widget;
            const productSection = productContainer?.closest('section') || productContainer?.closest('[data-section-id]') || productContainer?.closest('.shopify-section');
            if (productSection) {
              widget = productSection.querySelector('.loop-widget-container');
              if (widget) return widget;
            }
            return document.querySelector('.loop-widget-container');
          };
          
          const loopWidget = findLoopWidgetForCart();
          const loopSubscriptionSelected = loopWidget?.querySelector('[data-loop-widget-selling-plan-group].loop-widget-purchase-option-selected');
          
          if (loopSubscriptionSelected) {
            // Get the selling plan from Loop's dropdown
            const loopDropdown = loopWidget.querySelector('select[name="selling_plan"]');
            if (loopDropdown) {
              sellingPlan = loopDropdown.value;
            }
          } else {
            // Fallback to standard selling plan inputs
            const sellingPlanInput = form.querySelector('[name="selling_plan"]:checked') || form.querySelector('select[name="selling_plan"]') || form.querySelector('[name="selling_plan"]');
            sellingPlan = sellingPlanInput ? sellingPlanInput.value : null;
          }
          
          if (!variantId) {
            return;
          }
          
          // Build form data
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', quantity);
          if (sellingPlan) {
            formData.append('selling_plan', sellingPlan);
          }
          
          // Add loading state
          newBtn.classList.add('is-loading');
          newBtn.disabled = true;
          
          // Submit directly to cart
          fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          })
          .then(response => response.json())
          .then(data => {
            if (data.status) {
              // Error
              alert(data.message || 'Error adding to cart');
            } else {
              // Success - trigger cart update
              document.dispatchEvent(new CustomEvent('theme:cart:add', { bubbles: true }));
              
              // Open cart drawer or redirect
              const cartDrawer = document.querySelector('[data-cart-drawer]');
              if (cartDrawer) {
                document.dispatchEvent(new CustomEvent('theme:cart:toggle', { bubbles: true }));
              } else {
                window.location.href = '/cart';
              }
            }
          })
          .catch(function() {})
          .finally(() => {
            newBtn.classList.remove('is-loading');
            newBtn.disabled = false;
          });
        }, true);
      }
    }
    
    // Sync --selected CSS class to the actually-checked radio immediately.
    // The browser can restore a previously chosen quantity, making the HTML-rendered
    // default (qty=1) visually selected while a different radio is truly checked.
    (function syncInitialSelectedClass() {
      const checkedInput = selector.querySelector('[data-qty-discount-input]:checked');
      if (!checkedInput) return;
      const idx = Array.from(inputs).indexOf(checkedInput);
      if (idx < 0) return;
      options.forEach(opt => opt.classList.remove('qty-discount-selector__option--selected'));
      options[idx].classList.add('qty-discount-selector__option--selected');
    })();

    inputs.forEach((input, index) => {
      input.addEventListener('change', function() {
        const quantity = parseInt(this.dataset.quantity);
        const unitPrice = parseInt(this.dataset.unitPrice);

        // Apply subscription discount to display/ATC if subscription is active.
        // Qty selector tiles always show one-time prices; only the display and ATC reflect subscription.
        const displayUnitPrice = isSubscription ? Math.round(unitPrice * subscriptionDiscountRatio) : unitPrice;
        const displayTotal = displayUnitPrice * quantity;

        // Update selected state
        options.forEach(opt => opt.classList.remove('qty-discount-selector__option--selected'));
        options[index].classList.add('qty-discount-selector__option--selected');

        // Update ALL quantity inputs in the form to ensure correct value is submitted.
        // Do NOT dispatch 'change' or 'input' on qty inputs — the theme's onQuantityChange
        // handler picks those up, calls pushState → updateSaleTextSubscription (null crash)
        // and re-fires theme:variant:change, causing a cascade loop.
        allQtyInputs.forEach((qtyEl) => {
          qtyEl.value = quantity;
        });

        // Compare price = current variant's base (no-discount) price × quantity
        const comparePrice = basePrice * quantity;

        // Update price displays (product price area)
        updatePriceDisplay(priceElements, displayTotal);

        // Update ATC button price with compare price
        updateAtcPrice(displayTotal, comparePrice);

        // Update compare price and discount badge
        updateCompareAndDiscount(quantity, displayUnitPrice, displayTotal, comparePrice);
        
        // Always sync Loop widget to the current card's unit price.
        // For qty=1, unitPrice equals the base variant price — this resets
        // Loop back after a discounted tier was previously selected.
        const percentageOff = parseInt(this.dataset.percentageOff) || 0;
        updateLoopWidgetPrices(unitPrice);
        
        // Dispatch custom event for subscription form and other listeners
        const event = new CustomEvent('qty-discount:change', {
          bubbles: true,
          detail: {
            quantity: quantity,
            unitPrice: unitPrice,
            totalPrice: displayTotal,
            discount: parseInt(this.dataset.discount) || 0,
            selector: selector
          }
        });
        selector.dispatchEvent(event);
        document.dispatchEvent(event);
      });
    });
    
    // Update ATC button price and compare price
    function updateAtcPrice(price, comparePrice) {
      const atcButton = productContainer?.querySelector('[data-add-to-cart]');
      if (!atcButton) return;
      
      const hasDiscount = comparePrice && comparePrice > price;
      
      // Update main price
      const atcPriceEl = atcButton.querySelector('[data-product-price]');
      if (atcPriceEl) {
        const formattedPrice = formatMoney(price);
        const mwPrice = atcPriceEl.querySelector('.mw-price');
        if (mwPrice) {
          mwPrice.textContent = formattedPrice;
        } else {
          atcPriceEl.textContent = formattedPrice;
        }
        
        // Add/remove sale class
        if (hasDiscount) {
          atcPriceEl.classList.add('product__price--sale');
        } else {
          atcPriceEl.classList.remove('product__price--sale');
        }
      }
      
      // Update compare price in ATC
      let atcCompareEl = atcButton.querySelector('[data-compare-price]');
      
      if (hasDiscount) {
        // Create compare price element if it doesn't exist
        if (!atcCompareEl) {
          const priceWrapper = atcButton.querySelector('[data-price-wrapper]');
          if (priceWrapper) {
            atcCompareEl = document.createElement('s');
            atcCompareEl.setAttribute('data-compare-price', '');
            atcCompareEl.className = 'product__price--compare';
            atcCompareEl.innerHTML = '<span class="mw-price"></span>';
            // Insert after the main price
            const mainPrice = priceWrapper.querySelector('[data-product-price]');
            if (mainPrice && mainPrice.nextSibling) {
              priceWrapper.insertBefore(atcCompareEl, mainPrice.nextSibling);
            } else if (mainPrice) {
              priceWrapper.appendChild(atcCompareEl);
            }
          }
        }
        
        if (atcCompareEl) {
          const mwPrice = atcCompareEl.querySelector('.mw-price');
          if (mwPrice) {
            mwPrice.textContent = formatMoney(comparePrice);
          } else {
            atcCompareEl.textContent = formatMoney(comparePrice);
          }
          // Show compare price - remove hidden class and set display
          atcCompareEl.classList.remove('hidden');
          atcCompareEl.style.display = '';
        }
      } else if (atcCompareEl) {
        // Hide compare price if no discount
        atcCompareEl.classList.add('hidden');
        atcCompareEl.style.display = 'none';
      }
    }
    
    // Update compare price and discount badge
    function updateCompareAndDiscount(quantity, unitPrice, totalPrice, comparePrice) {
      // Calculate total discount percentage from original price
      const totalDiscountPercent = Math.round((1 - (totalPrice / comparePrice)) * 100);
      
      // Update compare price display
      const comparePriceEl = productContainer?.querySelector('[data-compare-price]');
      if (comparePriceEl) {
        const mwPrice = comparePriceEl.querySelector('.mw-price');
        if (mwPrice) {
          mwPrice.textContent = formatMoney(comparePrice);
        } else {
          comparePriceEl.textContent = formatMoney(comparePrice);
        }
        // Show compare price if there's a discount
        comparePriceEl.style.display = totalDiscountPercent > 0 ? '' : 'none';
      }
      
      // Update discount badge
      const discountBadgeEl = productContainer?.querySelector('[data-price-off-amount]');
      if (discountBadgeEl) {
        discountBadgeEl.textContent = totalDiscountPercent + '%';
      }
      
      // Show/hide the entire price-off wrapper
      const priceOffWrapper = productContainer?.querySelector('[data-price-off]');
      if (priceOffWrapper) {
        priceOffWrapper.style.display = totalDiscountPercent > 0 ? '' : 'none';
      }
      
      // Add sale class to price if discounted
      const priceWrapper = productContainer?.querySelector('[data-product-price]');
      if (priceWrapper && !priceWrapper.closest('[data-add-to-cart]')) {
        if (totalDiscountPercent > 0) {
          priceWrapper.classList.add('product__price--sale');
        } else {
          priceWrapper.classList.remove('product__price--sale');
        }
      }
    }
    
    // Update price display helper
    function updatePriceDisplay(elements, price) {
      if (!elements) return;
      
      elements.forEach(el => {
        // Skip subscription price watch elements
        if (el.closest('[data-subscription-watch-price]')) return;
        // Skip ATC button price (handled separately)
        if (el.closest('[data-add-to-cart]')) return;
        
        const formattedPrice = formatMoney(price);
        // Check for nested .mw-price span
        const mwPrice = el.querySelector('.mw-price');
        if (mwPrice) {
          mwPrice.textContent = formattedPrice;
        } else {
          el.textContent = formattedPrice;
        }
      });
    }
    
    // Format money helper
    function formatMoney(cents) {
      if (typeof theme !== 'undefined' && theme.Shopify && theme.Shopify.formatMoney) {
        return theme.Shopify.formatMoney(cents);
      }
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents);
      }
      // Fallback formatting
      const amount = (cents / 100).toFixed(2);
      return '$' + amount;
    }
    
    // Get discounted unit price: base_price × (100 - percentageOff) / 100
    function getDiscountedPrice(price, percentageOff) {
      const pct = parseInt(percentageOff) || 0;
      const mult = 100 - pct;
      return Math.round(price * mult / 100);
    }
    
    // Derive the subscription discount ratio from original prices so we can
    // apply it to any quantity-discounted unit price.
    let subscriptionDiscountRatio = (liquidSubscriptionPrice > 0 && originalBasePrice > 0)
      ? liquidSubscriptionPrice / originalBasePrice
      : 0.9;
    
    // Persistent state for Loop price enforcement — Loop's own JS can re-render
    // at any time so a one-shot write isn't enough.
    let loopTargetOnetimePrice = null;
    let loopTargetSubPrice = null;
    let loopPriceObserver = null;
    
    function applyLoopPrices() {
      const loopWidget = findLoopWidget();
      if (!loopWidget) return;
      
      const onetimeEl = loopWidget.querySelector('[id^="loop-widget-purchase-option-price-onetime-"]');
      const subEl = loopWidget.querySelector('[id^="loop-widget-purchase-option-price-id-"]');
      
      if (onetimeEl && loopTargetOnetimePrice !== null) {
        const desired = formatMoney(loopTargetOnetimePrice);
        if (onetimeEl.textContent.trim() !== desired) {
          onetimeEl.textContent = desired;
        }
      }
      if (subEl && loopTargetSubPrice !== null) {
        const desired = formatMoney(loopTargetSubPrice);
        if (subEl.textContent.trim() !== desired) {
          subEl.textContent = desired;
        }
      }
    }
    
    function ensureLoopPriceObserver() {
      if (loopPriceObserver) return;
      const loopWidget = findLoopWidget();
      if (!loopWidget) return;
      
      loopPriceObserver = new MutationObserver(function() {
        if (loopTargetOnetimePrice !== null || loopTargetSubPrice !== null) {
          applyLoopPrices();
        }
      });
      
      loopPriceObserver.observe(loopWidget, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
    
    function updateLoopWidgetPrices(discountedUnitPrice) {
      loopTargetOnetimePrice = discountedUnitPrice;
      loopTargetSubPrice = Math.round(discountedUnitPrice * subscriptionDiscountRatio);
      
      ensureLoopPriceObserver();
      applyLoopPrices();
      
      setTimeout(applyLoopPrices, 50);
      setTimeout(applyLoopPrices, 150);
      setTimeout(applyLoopPrices, 400);
    }
    
    function clearLoopPriceEnforcement() {
      loopTargetOnetimePrice = null;
      loopTargetSubPrice = null;
    }
    
    // Listen for variant changes
    const variantChangeHandler = function(e) {
      if (!e.detail || !e.detail.variant) return;

      // The theme fires this event on the section container (an ancestor of [data-product]).
      // Accept the event if e.target is our product container, an ancestor of it, or a descendant.
      const selectorProduct = selector.closest('[data-product]');
      if (selectorProduct) {
        const related = e.target === selectorProduct
          || e.target.contains(selectorProduct)
          || selectorProduct.contains(e.target);
        if (!related) return;
      }

      const newVariant = e.detail.variant;
      const newPrice = newVariant.price;
      basePrice = newPrice;
      selector.dataset.basePrice = newPrice;

      // Recalculate subscription ratio for this variant
      if (newVariant.selling_plan_allocations && newVariant.selling_plan_allocations.length > 0 && newPrice > 0) {
        const newSubPrice = newVariant.selling_plan_allocations[0].price;
        subscriptionDiscountRatio = newSubPrice / newPrice;
      }

      // Update count data for the new variant
      currentVariantId = String(newVariant.id);
      selector.dataset.variantId = currentVariantId;
      const countData = getVariantCountData(currentVariantId);
      if (countData) {
        currentBaseCount = countData.base_count || 0;
        currentCountLabel = countData.count_label || null;
        currentQtyName = parseQtyName(countData.qty_name);
      }

      // Update each option's price, badge, and count label
      updateAllOptionPrices();
    };
    
    // Helper function to update all option prices based on current base price
    function updateAllOptionPrices() {
      inputs.forEach((input, i) => {
        const percentageOff = parseInt(input.dataset.percentageOff) || 0;
        const discountedPrice = getDiscountedPrice(basePrice, percentageOff);

        input.dataset.unitPrice = discountedPrice;
        input.dataset.discount = percentageOff;

        const priceEl = options[i].querySelector('.qty-discount-selector__price');
        if (priceEl) {
          const newText = formatMoney(discountedPrice) + ' each';
          priceEl.textContent = newText;
        }

        const badgeEl = options[i].querySelector('.qty-discount-selector__badge');
        if (badgeEl && percentageOff > 0) {
          badgeEl.textContent = 'Save ' + percentageOff + '%';
        }

        // Update the quantity name / title (e.g. "1 Pouch" / "3 Pouches" -> "1 Box" / "3 Boxes")
        // per variant. qty=1 uses singular, tiers use plural.
        const qtyTitleEl = options[i].querySelector('.qty-discount-selector__qty');
        if (qtyTitleEl && currentQtyName) {
          const qty = parseInt(input.dataset.quantity) || 1;
          const name = qty > 1 ? currentQtyName.plural : currentQtyName.singular;
          qtyTitleEl.textContent = qty + ' ' + name;
        }

        // Update count label (e.g. "12 Bags", "36 Bags")
        const countEl = options[i].querySelector('.qty-discount-selector__count');
        if (currentBaseCount && currentCountLabel) {
          const qty = parseInt(input.dataset.quantity) || 1;
          const countText = (qty * currentBaseCount) + ' ' + currentCountLabel;
          if (countEl) {
            countEl.textContent = countText;
            countEl.style.display = '';
          } else {
            // Count line wasn't rendered server-side — create it
            const qtyEl = options[i].querySelector('.qty-discount-selector__qty');
            if (qtyEl) {
              const newCountEl = document.createElement('span');
              newCountEl.className = 'qty-discount-selector__count';
              newCountEl.textContent = countText;
              qtyEl.insertAdjacentElement('afterend', newCountEl);
            }
          }
        } else if (countEl) {
          countEl.style.display = 'none';
        }
      });
      
      // Directly update display for the checked card without dispatching DOM events
      // (dispatching 'change' would cascade: qty input → theme onQuantityChange → theme:variant:change → loop)
      const checkedInput = selector.querySelector('[data-qty-discount-input]:checked');
      if (checkedInput) {
        const qty = parseInt(checkedInput.dataset.quantity);
        const unitPrice = parseInt(checkedInput.dataset.unitPrice);
        const displayUnitPrice = isSubscription ? Math.round(unitPrice * subscriptionDiscountRatio) : unitPrice;
        const displayTotal = displayUnitPrice * qty;
        const compareTotal = basePrice * qty;
        updatePriceDisplay(priceElements, displayTotal);
        updateAtcPrice(displayTotal, compareTotal);
        updateCompareAndDiscount(qty, displayUnitPrice, displayTotal, compareTotal);
        updateLoopWidgetPrices(unitPrice);
      }
    }
    
    document.addEventListener('theme:variant:change', variantChangeHandler);
    
    // ==========================================
    // Loop Subscription Widget Integration
    // ==========================================
    
    // Find the Loop widget - it might be outside productContainer as a sibling app block
    function findLoopWidget() {
      // First try within product container
      let widget = productContainer?.querySelector('.loop-widget-container');
      if (widget) return widget;
      
      // Try finding in the main product section (Loop widget is often a sibling block)
      const productSection = productContainer?.closest('section') || productContainer?.closest('[data-section-id]') || productContainer?.closest('.shopify-section');
      if (productSection) {
        widget = productSection.querySelector('.loop-widget-container');
        if (widget) return widget;
      }
      
      // Try finding any Loop widget on the page (for single product pages)
      widget = document.querySelector('.loop-widget-container');
      return widget;
    }
    
    // Function to get subscription price from Loop widget or product data
    function getLoopSubscriptionPrice() {
      const loopWidgetContainer = findLoopWidget();
      
      if (loopWidgetContainer) {
        // Find the selected subscription option's price
        const selectedSubscription = loopWidgetContainer.querySelector('[data-loop-widget-selling-plan-group].loop-widget-purchase-option-selected');
        if (selectedSubscription) {
          const priceEl = selectedSubscription.querySelector('.loop-widget-purchase-option-price');
          if (priceEl) {
            // Parse price like "$43.16" to cents
            const priceText = priceEl.textContent.trim();
            const priceMatch = priceText.match(/[\d,.]+/);
            if (priceMatch) {
              const price = parseFloat(priceMatch[0].replace(',', '')) * 100;
              return Math.round(price);
            }
          }
        }
      }
      
      // Fallback: Try to get from product JSON (window.LOOP_WIDGET)
      if (window.LOOP_WIDGET) {
        const productId = Object.keys(window.LOOP_WIDGET)[0];
        const loopData = window.LOOP_WIDGET[productId];
        if (loopData?.product?.variants?.[0]?.selling_plan_allocations?.[0]?.price) {
          return loopData.product.variants[0].selling_plan_allocations[0].price;
        }
      }
      
      // Final fallback: Use pre-calculated Liquid price
      if (liquidSubscriptionPrice > 0) {
        return liquidSubscriptionPrice;
      }
      
      return null;
    }
    
    // Function to handle Loop widget selection changes
    function handleLoopWidgetChange() {
      const loopWidgetContainer = findLoopWidget();
      if (!loopWidgetContainer) return;
      
      // Check if subscription is selected
      const isSubscriptionSelected = loopWidgetContainer.querySelector('[data-loop-widget-selling-plan-group].loop-widget-purchase-option-selected') !== null;
      const isOneTimeSelected = loopWidgetContainer.querySelector('[data-loop-widget-onetime-purchase-option].loop-widget-purchase-option-selected') !== null;
      
      if (isSubscriptionSelected) {
        isSubscription = true;
      } else if (isOneTimeSelected) {
        isSubscription = false;
      }

      // Update display directly — do NOT dispatch DOM events here.
      // A DOM 'change' on the radio bubbles to qtyInput, which triggers the theme's
      // onQuantityChange → pushState → updateSaleTextSubscription (crashes on null)
      // and also re-fires theme:variant:change, creating a cascade loop.
      const checkedInput = selector.querySelector('[data-qty-discount-input]:checked');
      if (checkedInput) {
        const qty = parseInt(checkedInput.dataset.quantity);
        const unitPrice = parseInt(checkedInput.dataset.unitPrice);
        const displayUnitPrice = isSubscription ? Math.round(unitPrice * subscriptionDiscountRatio) : unitPrice;
        const displayTotal = displayUnitPrice * qty;
        const compareTotal = basePrice * qty;
        updatePriceDisplay(priceElements, displayTotal);
        updateAtcPrice(displayTotal, compareTotal);
        updateCompareAndDiscount(qty, displayUnitPrice, displayTotal, compareTotal);
        updateLoopWidgetPrices(unitPrice);
      }
    }
    
    // Set up observers for Loop widget
    let loopWidgetRetryCount = 0;
    const maxLoopWidgetRetries = 50; // Max 5 seconds (50 × 100ms)
    
    function setupLoopWidgetObserver() {
      const loopWidgetContainer = findLoopWidget();
      
      // Check if Loop widget has loaded its content (has purchase options)
      const hasPurchaseOptions = loopWidgetContainer?.querySelector('[data-loop-widget-purchase-option]');
      
      if (!loopWidgetContainer || !hasPurchaseOptions) {
        loopWidgetRetryCount++;
        if (loopWidgetRetryCount < maxLoopWidgetRetries) {
          // Loop widget might not be loaded yet, retry
          setTimeout(setupLoopWidgetObserver, 100);
        }
        return;
      }
      
      // Click listener on entire Loop widget container (captures all clicks including radio)
      loopWidgetContainer.addEventListener('click', function(e) {
        // IMPORTANT: Skip clicks on dropdowns/selects to not interfere with frequency selector
        if (e.target.closest('select') || e.target.tagName === 'SELECT' || e.target.tagName === 'OPTION') {
          return; // Let the dropdown handle its own events
        }
        
        // Check if clicked within a purchase option (but not on a dropdown)
        const purchaseOption = e.target.closest('[data-loop-widget-purchase-option]');
        if (purchaseOption) {
          // Delay to let Loop widget update its state and classes
          setTimeout(handleLoopWidgetChange, 100);
        }
      }, true); // Use capture phase to catch events before Loop widget might stop propagation
      
      // Also add direct click listeners on radio divs (in case click doesn't bubble)
      const radioElements = loopWidgetContainer.querySelectorAll('.loop-widget-purchase-option-radio');
      radioElements.forEach(radio => {
        radio.addEventListener('click', function() {
          setTimeout(handleLoopWidgetChange, 100);
        });
      });
      
      // Also observe class changes on the widget for programmatic selection changes
      const observer = new MutationObserver(function(mutations) {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            if (target.hasAttribute('data-loop-widget-purchase-option')) {
              setTimeout(handleLoopWidgetChange, 100);
              break;
            }
          }
        }
      });
      
      // Observe all purchase options for class changes
      const purchaseOptions = loopWidgetContainer.querySelectorAll('[data-loop-widget-purchase-option]');
      purchaseOptions.forEach(option => {
        observer.observe(option, { attributes: true, attributeFilter: ['class'] });
      });
      
      // Also listen for selling plan dropdown changes
      const sellingPlanDropdown = loopWidgetContainer.querySelector('select[name="selling_plan"]');
      if (sellingPlanDropdown) {
        sellingPlanDropdown.addEventListener('change', function() {
          setTimeout(handleLoopWidgetChange, 100);
        });
      }
      
      // Initial check - see if subscription is already selected (Loop often defaults to subscription)
      // Wait for widget to fully initialize, then check state
      setTimeout(function() {
        const loopWidget = findLoopWidget();
        const isSubSelected = loopWidget?.querySelector('[data-loop-widget-selling-plan-group].loop-widget-purchase-option-selected');
        
        if (isSubSelected) {
          isSubscription = true;
          const checkedInput = selector.querySelector('[data-qty-discount-input]:checked');
          if (checkedInput) {
            const qty = parseInt(checkedInput.dataset.quantity);
            const unitPrice = parseInt(checkedInput.dataset.unitPrice);
            const displayUnitPrice = Math.round(unitPrice * subscriptionDiscountRatio);
            const displayTotal = displayUnitPrice * qty;
            const compareTotal = basePrice * qty;
            updatePriceDisplay(priceElements, displayTotal);
            updateAtcPrice(displayTotal, compareTotal);
            updateCompareAndDiscount(qty, displayUnitPrice, displayTotal, compareTotal);
            updateLoopWidgetPrices(unitPrice);
          }
        }
      }, 500);
    }
    
    // Initialize Loop widget observer after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupLoopWidgetObserver);
    } else {
      setupLoopWidgetObserver();
    }
  }
  
  // Re-initialize on dynamic content load (e.g., quick view)
  document.addEventListener('theme:quick-view:loaded', function() {
    document.querySelectorAll('[data-qty-discount-selector]').forEach(initSelector);
  });
})();